import { useCallback, useEffect, useLayoutEffect } from 'react';

import cx from 'classnames';

import {
  stopCalculateMargin,
  updateOrderType,
  resetQuantityInput,
} from 'actions/placeorder';
import { isAuthenticated } from 'actions/user';
import { IS_DEMO, MARGIN_TYPES } from 'constants/constants';
import { PLACE_ORDER_TYPE, TRADE_PAGE_VERIFICATION_FLAGS } from 'constants/enums';
import { getProductSymbol } from 'helpers/utils';
import useTotalAccountEquity from 'hooks/componentHooks/useTotalAccountEquity';
import useKYC from 'hooks/useKYC';
import useCustomMediaQuery from 'hooks/useMediaQuery';
import i18n from 'i18n/config';
import { APP_DRAWER_TAB_ID_MAPPING } from 'reducers/initialstate/appDrawer';
import {
  appDrawerSelector,
  showOrderOnChartSelector,
  userMarginModeSelector,
} from 'selectors/otherSelectors';
import { quantityInputValueInContract } from 'selectors/placeOrderSelector';
import {
  isOnlineSelector,
  isSocketReconnecting,
  selectedProductSelector,
} from 'selectors/tradeSelectors';
import { showPlaceOrderSectionSelector } from 'selectors/tradingViewSelector';
import { selectTradePagesVerificationMessageFlag } from 'selectors/user';
import { useAppDispatch, useAppSelector } from 'storeHooks';

import KycRefreshExpired from '../../KycRefreshExpiredPlaceOrder/KycRefreshExpired';
import ConfirmationModals from '../ConfirmationModals';
import Form from '../Form';
import getMarginPortfolio from '../MarginComponent/marginPortfolioThunk';
import QuantityInput from '../QuantityInput';
import { resetErrors } from '../QuantityInput/handleErrors.thunk';
import VerifyButton from '../SubmitButton/VerifyButton';
import BuyButton from './BuyButton';
import styles from './ChartScreen.module.scss';
import ErrorMessage from './ErrorMessages';
import SellButton from './SellButton';

const ChartScreen = () => {
  const isLoggedIn = useAppSelector(({ user }) => isAuthenticated(user));
  const { isVerified, isReduceOnlyEnable, isKycRefreshRequired } = useKYC();
  const isInternetConnected = useAppSelector(isOnlineSelector);
  const isSocketDisconnected = useAppSelector(isSocketReconnecting);
  const showOnChartScreen = useAppSelector(showOrderOnChartSelector);
  const totalBalance = useTotalAccountEquity();
  const dispatch = useAppDispatch();
  let placeHolderValue = i18n.t('trading:quantity');
  const { whenBelowXss, isDesktop } = useCustomMediaQuery();
  const { activeTab } = useAppSelector(appDrawerSelector);
  const showPlaceOrderSection = useAppSelector(showPlaceOrderSectionSelector);
  const selectedProduct = useAppSelector(selectedProductSelector);
  const product = getProductSymbol(selectedProduct);

  const verificationMessageFlag = useAppSelector(selectTradePagesVerificationMessageFlag);
  const showGetVerified =
    verificationMessageFlag === TRADE_PAGE_VERIFICATION_FLAGS.GET_VERIFIED;
  const marginMode = useAppSelector(userMarginModeSelector);
  const orderSize = useAppSelector(quantityInputValueInContract);

  if (whenBelowXss) {
    placeHolderValue = ' ';
  }

  const isVerifiedUser = isVerified || isKycRefreshRequired || IS_DEMO;

  // NOTE:
  // We're using useLayoutEffect instead of useEffect because this component is wrapped inside <Freeze />.
  // When the component unfreezes (i.e., tab becomes active again), useEffect does NOT run,
  // useEffect is asynchronous after painting, which is skipped in unfreeze context to avoid duplicate side-effects.
  // useEffect doesn’t fire on unfreeze
  // useLayoutEffect is synchronous before paint, and tied to render cycles — so it executes when you unfreeze and React re-renders the component.
  // but useLayoutEffect DOES run if the component re-renders.
  // useLayoutEffect still works on unfreeze,fires on unfreeze when render resumes.
  // This ensures our logic below runs correctly on unfreeze.
  useLayoutEffect(() => {
    if (!isDesktop) {
      dispatch(
        updateOrderType(PLACE_ORDER_TYPE.MARKET, { shouldUpdatePreference: false })
      );
    }
  }, [dispatch, isDesktop]);

  const handleRefreshMargin = useCallback(() => {
    if (marginMode === MARGIN_TYPES.PORTFOLIO) {
      dispatch(getMarginPortfolio());
    }
  }, [marginMode, dispatch]);

  useEffect(() => {
    handleRefreshMargin();
    return () => {
      dispatch(stopCalculateMargin());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // update margin when order size changes
    handleRefreshMargin();
  }, [handleRefreshMargin, orderSize]);
  // reset quantity input when product changes- WEB-5927
  // Checked peformnce by throttling cpu-looks fine
  // revisit this when picking place order refctor ticket.
  useLayoutEffect(() => {
    dispatch(resetQuantityInput());
    dispatch(resetErrors());
  }, [product, dispatch]);

  if (
    !isLoggedIn ||
    !showOnChartScreen ||
    activeTab !== APP_DRAWER_TAB_ID_MAPPING.CHART_ORDERBOOK ||
    isDesktop ||
    !showPlaceOrderSection
  ) {
    return null;
  }

  // if the user is verified or reduceonly mode is enabled or if the user is Indian and level3 verification is done - show Buy button
  // else show Get Verified button
  const isTradingEnabled =
    (isVerifiedUser && totalBalance) || IS_DEMO || isReduceOnlyEnable;

  // TODO: move global exchange logic to  selector
  if (isReduceOnlyEnable && !showGetVerified) {
    return <KycRefreshExpired />;
  }

  return (
    (<Form
      className={cx('flex-set', styles.form, {
        [styles.unVerifiedContainer]: !isTradingEnabled || showGetVerified,
        [styles.verifiedContainer]: isTradingEnabled,
      })}
    >
      {isTradingEnabled && !showGetVerified && (
        <>
          <div className={styles.input} data-palette="ChartScreen">
            <QuantityInput placeholder={placeHolderValue} />
            <ErrorMessage />
          </div>
          <SellButton disabled={Boolean(!isInternetConnected || isSocketDisconnected)} />
          <BuyButton disabled={Boolean(!isInternetConnected || isSocketDisconnected)} />
        </>
      )}
      {(!isTradingEnabled || showGetVerified) && <VerifyButton />}
      <ConfirmationModals />
    </Form>)
  );
};

export default ChartScreen;
